/**
 * PHASE 8 — DOM layer for sky labels.
 *
 * Positions absolutely positioned spans at the projected pixels. It keeps a
 * pool indexed by object id so labels are created once and just re-positioned
 * (or hidden) per frame.
 *
 * Per-frame DOM writes are the main CPU cost on real phones, so sync() only
 * touches the DOM when a label's projected position, text or visibility has
 * actually changed (dirty check). A stationary sky therefore costs zero
 * layout thrash after the first frame.
 */

import type { ProjectedLabel } from "./skyLabels";

interface SpanState {
  x: number;
  y: number;
  text: string;
  type: string;
  visible: boolean;
}

export class LabelLayer {
  private spans = new Map<string, { el: HTMLElement; state: SpanState }>();

  constructor(private container: HTMLElement) {}

  sync(labels: readonly ProjectedLabel[]): void {
    const seen = new Set<string>();
    for (const label of labels) {
      seen.add(label.id);
      let entry = this.spans.get(label.id);
      if (!entry) {
        const el = document.createElement("span");
        el.className = "sky-label";
        el.dataset.id = label.id;
        this.container.appendChild(el);
        entry = { el, state: { x: NaN, y: NaN, text: "", type: "", visible: false } };
        this.spans.set(label.id, entry);
      }
      const state = entry.state;
      const x = Math.round(label.xPx * 10) / 10;
      const y = Math.round(label.yPx * 10) / 10;
      const dirty = state.x !== x || state.y !== y || state.text !== label.name || state.type !== label.type;
      if (dirty) {
        if (state.text !== label.name) entry.el.textContent = label.name;
        if (state.type !== label.type) entry.el.dataset.type = label.type;
        if (state.x !== x || state.y !== y) {
          entry.el.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px)`;
        }
        state.x = x;
        state.y = y;
        state.text = label.name;
        state.type = label.type;
      }
      if (state.visible !== label.visible) {
        entry.el.style.opacity = label.visible ? "1" : "0";
        state.visible = label.visible;
      }
    }
    for (const [id, entry] of this.spans) {
      if (!seen.has(id) && entry.state.visible) {
        entry.el.style.opacity = "0";
        entry.state.visible = false;
      }
    }
  }

  clear(): void {
    this.container.replaceChildren();
    this.spans.clear();
  }
}
